import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api';

/**
 * Один загрузчик на всю админку. Раньше каждый модуль писал свой, и в ветке
 * ошибки состояние загрузки не сбрасывалось: кассир открывал недоступный раздел
 * и видел «Загрузка…», которая висела вечно под сообщением об отказе.
 */
export type ResourceState<T> =
  | { readonly status: 'loading'; readonly data?: T; readonly error?: undefined }
  | { readonly status: 'ready'; readonly data: T; readonly error?: undefined }
  | { readonly status: 'error'; readonly data?: T; readonly error: ResourceError };

export interface ResourceError {
  readonly code: string;
  readonly message: string;
  readonly status: number;
  /** Отказ по правам показывается иначе, чем поломка: это не ошибка, а граница роли. */
  readonly forbidden: boolean;
}

export interface Resource<T> {
  readonly status: ResourceState<T>['status'];
  readonly data: T | undefined;
  readonly error: ResourceError | undefined;
  readonly loading: boolean;
  readonly reload: () => void;
}

export function toResourceError(err: unknown): ResourceError {
  if (err instanceof ApiError) {
    return {
      code: err.code,
      message: err.message,
      status: err.status,
      forbidden: err.status === 403,
    };
  }
  return {
    code: 'E00.internal',
    message: (err as Error)?.message ?? 'Не удалось выполнить запрос',
    status: 0,
    forbidden: false,
  };
}

export function useResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  options: { readonly enabled?: boolean } = {},
): Resource<T> {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<ResourceState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'ready', data: undefined as T });
      return undefined;
    }
    let alive = true;
    setState((current) => ({ status: 'loading', ...(current.data ? { data: current.data } : {}) }));
    loadRef
      .current()
      .then((data) => {
        if (alive) setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        // Ключевое: ошибка тоже завершает загрузку.
        if (alive) setState({ status: 'error', error: toResourceError(err) });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    status: state.status,
    data: state.data,
    error: state.error,
    loading: state.status === 'loading',
    reload,
  };
}

/**
 * Адрес страницы должен переживать F5 и пересылаться коллеге. Обе SPA раньше
 * держали навигацию только в useState: один URL на всё приложение, обновление
 * страницы сбрасывало на первый экран.
 */
export interface Located {
  readonly module: string;
  readonly tab: string | null;
  /**
   * Третий сегмент адреса — идентификатор сущности внутри вкладки.
   * Нужен, чтобы карточку клиента можно было переслать ссылкой: без него
   * коллеге приходится диктовать двадцатишестисимвольный ULID голосом.
   */
  readonly detail: string | null;
}

function readHash(): Located {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const [module = '', tab = '', detail = ''] = raw.split('/');
  return {
    module: decodeURIComponent(module),
    tab: tab ? decodeURIComponent(tab) : null,
    detail: detail ? decodeURIComponent(detail) : null,
  };
}

export function useHashLocation(): [Located, (next: Located) => void] {
  const [location, setLocation] = useState<Located>(() => readHash());

  useEffect(() => {
    const onChange = () => setLocation(readHash());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback((next: Located) => {
    const parts = [encodeURIComponent(next.module)];
    if (next.tab) parts.push(encodeURIComponent(next.tab));
    if (next.tab && next.detail) parts.push(encodeURIComponent(next.detail));
    const hash = `#/${parts.join('/')}`;
    if (window.location.hash !== hash) window.location.hash = hash;
    else setLocation(next);
  }, []);

  return [location, navigate];
}

/**
 * Вкладка внутри модуля: тоже в адресе, плюс контекст модуля не теряется при
 * переключении (раньше введённый идентификатор клиента стирался при уходе на
 * соседнюю вкладку и обратно).
 */
export function useTab<T extends string>(
  moduleKey: string,
  tabs: readonly T[],
  fallback: T,
): [T, (tab: T) => void] {
  const [location, navigate] = useHashLocation();
  const fromHash = tabs.find((t) => t === location.tab);
  const active = fromHash ?? fallback;

  // Уход на соседнюю вкладку сбрасывает третий сегмент: идентификатор клиента
  // в адресе «Сегменты» не значит ничего и только сбивает с толку.
  const setTab = useCallback(
    (tab: T) => navigate({ module: moduleKey, tab, detail: null }),
    [moduleKey, navigate],
  );

  useEffect(() => {
    if (location.module === moduleKey && !fromHash) {
      navigate({ module: moduleKey, tab: active, detail: null });
    }
  }, [location.module, moduleKey, fromHash, active, navigate]);

  return [active, setTab];
}

/**
 * Идентификатор сущности в адресе вкладки: `#/identity/card/<customerId>`.
 * Возвращает пустую строку, если адрес указывает на другую вкладку — тогда
 * карточка честно показывает «выберите клиента», а не остатки прошлого выбора.
 */
export function useDetail(moduleKey: string, tabKey: string): [string, (id: string) => void] {
  const [location, navigate] = useHashLocation();
  const here = location.module === moduleKey && location.tab === tabKey;
  const detail = here ? (location.detail ?? '') : '';

  const setDetail = useCallback(
    (id: string) => navigate({ module: moduleKey, tab: tabKey, detail: id || null }),
    [moduleKey, tabKey, navigate],
  );

  return [detail, setDetail];
}
